import { FC, useState, useCallback, useContext } from 'react';

import { Button, OpenedEyeIcon, ShareIcon } from '@smartshell/ui-kit';
import cn from 'classnames';

import { LocalizationContext } from 'localization';
import { ETheme, ThemeContext } from 'theme';
import { formatCompactNumber } from 'utils';
import { getAdvertisementImageUrl } from 'utils/advertisementImage';

import { IImageCarouselProps } from './interfaces';
import s from './Slider.module.scss';

export const Slider: FC<IImageCarouselProps> = ({ images, view }) => {
  const { t } = useContext(LocalizationContext);
  const [currentIndex, setCurrentIndex] = useState(0);
  const { theme } = useContext(ThemeContext);

  const goToPrevious = useCallback(
    (e) => {
      e.stopPropagation();
      setCurrentIndex((prevIndex) =>
        prevIndex === 0 ? images.length - 1 : prevIndex - 1
      );
    },
    [images.length]
  );

  const goToNext = useCallback(
    (e) => {
      e.stopPropagation();

      setCurrentIndex((prevIndex) =>
        prevIndex === images.length - 1 ? 0 : prevIndex + 1
      );
    },
    [images.length]
  );

  const goToImage = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  const handleShare = useCallback(() => {
    const currentImage = getAdvertisementImageUrl(images[currentIndex]);
    if (currentImage) {
      window.open(currentImage, '_blank');
    }
  }, [images, currentIndex]);

  return (
    <div className={s.carousel}>
      <div className={s.mainImageContainer}>
        <div className={s.view_block}>
          <OpenedEyeIcon />
          <span className={s.text}>{formatCompactNumber(view, t)}</span>
        </div>
        <div className={s.share}>
          <ShareIcon size="l" color="secondary" />
        </div>
        {images.length > 1 && (
          <Button
            className={cn(s.navButton, s.prevButton)}
            variant="secondary"
            onlyIcon
            iconName="BackwardIcon"
            onClick={goToPrevious}
          />
        )}

        {images?.[0].length ? (
          <div
            onClick={handleShare}
            className={s.blur}
            style={{
              backgroundImage: `url(${getAdvertisementImageUrl(
                images[currentIndex],
                432
              )})`,
            }}
          >
            <img
              className={s.mainImage}
              src={getAdvertisementImageUrl(images[currentIndex], 432)}
              srcSet={`
                ${getAdvertisementImageUrl(images[currentIndex], 432)} 1x,
                ${getAdvertisementImageUrl(images[currentIndex], 864)} 2x,
                ${getAdvertisementImageUrl(images[currentIndex], 1296)} 3x,
      `}
              alt={images[currentIndex]}
            />
          </div>
        ) : (
          <img
            className={s.image}
            src={`/images/Advertisement/emptyAdvertisementImage${
              theme === ETheme.DARK ? 'Dark' : 'White'
            }.png`}
            alt="placeholder_image"
          />
        )}

        {images.length > 1 && (
          <Button
            className={cn(s.navButton, s.nextButton)}
            variant="secondary"
            onlyIcon
            iconName="NextArrowIcon"
            onClick={goToNext}
          />
        )}
      </div>

      {images.length > 0 && images?.[0].length && (
        <div className={s.thumbnailsContainer}>
          {images.map((image, index) => (
            <div
              key={image}
              className={cn(s.thumbnail, {
                [s.active]: index === currentIndex,
              })}
              onClick={() => goToImage(index)}
            >
              <div
                className={s.blur}
                style={{
                  backgroundImage: `url(${getAdvertisementImageUrl(
                    image,
                    80
                  )})`,
                }}
              >
                <img
                  src={getAdvertisementImageUrl(image, 80)}
                  srcSet={`
                ${getAdvertisementImageUrl(image, 80)} 1x,
                ${getAdvertisementImageUrl(image, 160)} 2x,
                ${getAdvertisementImageUrl(image, 240)} 3x,
      `}
                  alt={image}
                  className={s.thumbnailImage}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
